import { useRef, useState, useEffect } from 'react';
import globalHook, { type Store } from 'utils/globalHook';
import useCsrf, { type CsrfState } from 'effects/csrf';
import useDeepCompare from 'effects/useDeepCompare';
import api from 'utils/api';
import tixly from 'tixly/api';

interface TixlyProfile {
  url: string | null;
}

interface TixlyClient {
  id: string | number | null;
  name: string | null;
  email: string | null;
  hash: string | null;
  programs: unknown[];
  tixly: boolean;
  server: boolean;
  saved: boolean;
}

interface TixlyOrder {
  items: unknown[];
  expires: number;
  url: string | null;
}

/** What the Tixly iframe posts back (see tixly/api). */
interface TixlySessionData {
  profile?: string;
  user?: Partial<TixlyClient>;
  order: Partial<TixlyOrder>;
}

let busy = false;
let timeout: ReturnType<typeof setTimeout> | undefined;

const initialClient = window.initialClient ?? {
  state: null,
  clientActions: null,
};

const initialProfileState: TixlyProfile = {
  url: null,
};

const emptyClientState: TixlyClient = {
  id: null,
  name: null,
  email: null,
  hash: null,
  programs: [],
  tixly: false,
  server: false,
  saved: false,
};

const initialClientState: TixlyClient = {
  ...emptyClientState,
  ...initialClient.state,
};

const initialOrderState: TixlyOrder = {
  items: [],
  expires: 0,
  url: null,
};

const url = {
  current: '/tixly/client/current',
  login: '/tixly/client/login',
  ...initialClient.clientActions,
};

const profileActions = {
  update: (store: Store<TixlyProfile>, payload: Partial<TixlyProfile>) => {
    store.setState({
      ...store.state,
      ...payload,
    });
  },
};

const clientActions = {
  fetchFromServer: (store: Store<TixlyClient>) => {
    if (!busy) {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        busy = true;
        api<Partial<TixlyClient>>(url.current)
          .then(payload => {
            store.setState({
              ...store.state,
              ...payload,
            });
            busy = false;
          })
          .catch(() => {
            busy = false;
          });
      }, 240);
    }
  },

  saveToServer: (store: Store<TixlyClient>, csrf: CsrfState) => {
    return new Promise<Partial<TixlyClient>>((resolve, reject) => {
      if (!busy) {
        busy = true;
        api<Partial<TixlyClient>>(url.login, 'POST', {
          [csrf.name as string]: csrf.value,
          client: store.state,
        })
          .then(payload => {
            store.setState({
              ...store.state,
              ...payload,
            });
            busy = false;
            resolve(payload);
          })
          .catch(e => {
            busy = false;
            reject(e);
          });
      }
    });
  },

  update: (store: Store<TixlyClient>, payload: Partial<TixlyClient>) => {
    store.setState({
      ...store.state,
      ...payload,
    });
  },
};

const orderActions = {
  update: (store: Store<TixlyOrder>, payload: Partial<TixlyOrder>) => {
    store.setState({
      ...initialOrderState,
      ...payload,
    });
  },
};

const useGlobalProfile = globalHook(initialProfileState, profileActions);
const useGlobalClient = globalHook(initialClientState, clientActions);
const useGlobalOrder = globalHook(initialOrderState, orderActions);

export function useTixlyClient() {
  const [client] = useGlobalClient();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [init, setInit] = useState(false);

  useEffect(() => {
    setInit(true);
  }, [useDeepCompare(client)]);

  return client;
}

export function useTixlyOrder() {
  const [order] = useGlobalOrder();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [init, setInit] = useState(false);

  useEffect(() => {
    setInit(true);
  }, [useDeepCompare(order)]);

  return order;
}

export default (itixUrl: string) => {
  const ref = useRef<unknown>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [init, setInit] = useState(false);
  const [csrf, csrfclientActions] = useCsrf();
  const [profile, profileActions] = useGlobalProfile();
  const [client, clientActions] = useGlobalClient();
  const [order, orderActions] = useGlobalOrder();
  const clientId = client.id ?? null;

  useEffect(() => {
    const onChange = (data: TixlySessionData) => {
      if (data.profile) {
        profileActions.update({
          url: data.profile,
        });
      }

      if (data.user) {
        clientActions.update({
          ...data.user,
          saved: data.user.id === clientId,
          tixly: true,
        });
      } else {
        clientActions.update({
          ...emptyClientState,
          saved: clientId === null,
          tixly: true,
        });
      }

      orderActions.update(data.order);

      setInit(true);
    };

    tixly.setUrl(itixUrl);
    tixly.addEventListener(onChange);

    return function cleanup() {
      tixly.removeEventListener(onChange);
    };
  }, [itixUrl, clientId]);

  useEffect(() => {
    if (client.tixly && !client.saved) {
      clientActions
        .saveToServer(csrf)
        .then(() => {
          csrfclientActions.refresh();
        })
        .catch(() => {
          csrfclientActions.refresh();
        });
    }
  }, [useDeepCompare(client)]);

  return {
    ref,
    profile: profile.url ?? null,
    client,
    order,
    refresh: tixly.refresh,
  };
};
